import { CASE_STATUS } from "@/features/case/constants/caseStatus";
import { ECaseMatchStatus } from "@/types/enum";
import { TCaseMatches, TGetCaseDto } from "@/api/services/main/case/dtos/get-case.dto";

export const getActiveStepFromCaseStatus = (progressStatus?: number): number => {
  if (progressStatus === undefined || progressStatus === null) {
    return 1;
  }

  switch (progressStatus) {
    case CASE_STATUS.JOB_POSTED:
      return 0;
    case CASE_STATUS.PREPARING_PROPOSAL:
      return 1;
    case CASE_STATUS.PROPOSING_CANDIDATE:
      return 2;
    case CASE_STATUS.INTERVIEWING:
      return 3;
    case CASE_STATUS.OFFERED:
      return 4;
    case CASE_STATUS.WAITING_FOR_JOINING:
      return 5;
    case CASE_STATUS.RECRUITMENT_STOPPED:
      return 6;
    default:
      return 1;
  }
};

export const getStepLabelFromCaseStatus = (progressStatus?: number, caseMatches: TCaseMatches[] = [], isAllFailed?: boolean): string => {
  const stepIndex = getActiveStepFromCaseStatus(progressStatus);
  const allMatchesFailed = isAllFailed ?? areAllCaseMatchesFailed(caseMatches);
  const showRecruitmentEnded = progressStatus === CASE_STATUS.RECRUITMENT_STOPPED && allMatchesFailed;

  if (showRecruitmentEnded) {
    const stepLabels = ["求人情報登録", "人材推薦準備中", "人材推薦中", "面接選考中", "募集終了"];
    const adjustedIndex = Math.min(stepIndex, stepLabels.length - 1);
    return stepLabels[adjustedIndex] || stepLabels[1];
  }

  // Default step label
  const stepLabels = ["求人情報登録", "人材推薦準備中", "人材推薦中", "面接選考中", "内定", "入社待ち"];
  return stepLabels[stepIndex] || stepLabels[1];
};

export const areAllCaseMatchesFailed = (caseMatches: TCaseMatches[]): boolean => {
  if (caseMatches.length === 0) return false;

  const failedStatuses = [
    ECaseMatchStatus.FAILED,
    ECaseMatchStatus.CANDIDATE_WITHDRAWN,
    ECaseMatchStatus.OFFER_CANCELLED,
    ECaseMatchStatus.SELECTION_CANCELLED,
  ];

  return caseMatches.every((match) => {
    const status = match.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
    return failedStatuses.includes(status);
  });
};

// Check if all case matches are ALREADY_HIRED (入社済み)
export const areAllCaseMatchesAlreadyHired = (caseMatches: TCaseMatches[]): boolean => {
  if (caseMatches.length === 0) return false;

  return caseMatches.every((match) => {
    const status = match.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
    return status === ECaseMatchStatus.ALREADY_HIRED;
  });
};

// Check if all case matches are ALREADY_HIRED (入社済み) or failed statuses
export const areAllCaseMatchesHiredOrFailed = (caseMatches: TCaseMatches[]): boolean => {
  if (caseMatches.length === 0) return false;

  const failedStatuses = [
    ECaseMatchStatus.FAILED,
    ECaseMatchStatus.CANDIDATE_WITHDRAWN,
    ECaseMatchStatus.OFFER_CANCELLED,
    ECaseMatchStatus.SELECTION_CANCELLED,
  ];

  return caseMatches.every((match) => {
    const status = match.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
    return status === ECaseMatchStatus.ALREADY_HIRED || failedStatuses.includes(status);
  });
};

// Check if all case matches are OFFER_ACCEPTED (内定受諾) or failed statuses
export const areAllCaseMatchesAcceptedOrFailed = (caseMatches: TCaseMatches[]): boolean => {
  if (caseMatches.length === 0) return false;

  const failedStatuses = [
    ECaseMatchStatus.FAILED,
    ECaseMatchStatus.CANDIDATE_WITHDRAWN,
    ECaseMatchStatus.OFFER_CANCELLED,
    ECaseMatchStatus.SELECTION_CANCELLED,
  ];

  return caseMatches.every((match) => {
    const status = match.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
    return status === ECaseMatchStatus.OFFER_ACCEPTED || failedStatuses.includes(status);
  });
};

// Check if case matches have both failed and successful (OFFER_ACCEPTED/ALREADY_HIRED) statuses
export const hasFailedAndSuccessful = (caseMatches: TCaseMatches[]): boolean => {
  if (caseMatches.length === 0) return false;

  const failedStatuses = [
    ECaseMatchStatus.FAILED,
    ECaseMatchStatus.CANDIDATE_WITHDRAWN,
    ECaseMatchStatus.OFFER_CANCELLED,
    ECaseMatchStatus.SELECTION_CANCELLED,
  ];

  const hasFailed = caseMatches.some((match) => {
    const status = match.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
    return failedStatuses.includes(status);
  });

  const hasSuccessful = caseMatches.some((match) => {
    const status = match.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
    return status === ECaseMatchStatus.OFFER_ACCEPTED || status === ECaseMatchStatus.ALREADY_HIRED;
  });

  return hasFailed && hasSuccessful;
};

// Check if case is completed (step 6 - RECRUITMENT_STOPPED)
export const isCaseCompleted = (progressStatus?: number): boolean => {
  return progressStatus === CASE_STATUS.RECRUITMENT_STOPPED;
};

// Check if person in charge field should be locked (募集終了 only — 入社待ち still allows editing, same as 案件名/案件管理番号)
export const isPersonInChargeLockedStatus = (progressStatus?: number): boolean => {
  return progressStatus === CASE_STATUS.RECRUITMENT_STOPPED;
};

// Check if case can be edited based on status and case matches
export const canEditCase = (progressStatus?: number, caseMatches: TCaseMatches[] = []): boolean => {
  if (progressStatus === undefined || progressStatus === null) {
    return true;
  }

  // Rule: Step 6 (RECRUITMENT_STOPPED) always allows editing match info and status
  if (progressStatus === CASE_STATUS.RECRUITMENT_STOPPED) {
    return true;
  }

  // Rule: If all case matches are ALREADY_HIRED (入社済み) → no edit (case status should be 入社待ち)
  if (areAllCaseMatchesAlreadyHired(caseMatches)) {
    return false;
  }

  // Rule: If all are ALREADY_HIRED or failed (and no OFFER_ACCEPTED) → WAITING_FOR_JOINING (5) → no edit
  // But if there's mix of failed and OFFER_ACCEPTED/ALREADY_HIRED → still allows edit
  if (progressStatus === CASE_STATUS.WAITING_FOR_JOINING) {
    // If all are ALREADY_HIRED or failed (no OFFER_ACCEPTED) → no edit
    const hasOfferAccepted = caseMatches.some((match) => (match.status ?? ECaseMatchStatus.BEFORE_PROPOSAL) === ECaseMatchStatus.OFFER_ACCEPTED);
    if (areAllCaseMatchesHiredOrFailed(caseMatches) && !hasOfferAccepted) {
      return false;
    }
    // If there's mix of failed and OFFER_ACCEPTED/ALREADY_HIRED → allows edit
    if (hasFailedAndSuccessful(caseMatches)) {
      return true;
    }
  }

  // Rule: If all are OFFER_ACCEPTED or failed → WAITING_FOR_JOINING (5) → still allows edit
  // (This case allows edit, so we don't need to check it)

  return true;
};

/**
 * Check if case can be edited using backend-computed flags (pagination-safe).
 * Uses pre-computed flags from backend that consider ALL case matches across ALL pages.
 */
export const canEditCaseFromInfo = (caseInfo?: TGetCaseDto): boolean => {
  if (!caseInfo) {
    return true;
  }

  const progressStatus = caseInfo.progressStatus;

  if (progressStatus === undefined || progressStatus === null) {
    return true;
  }

  // Rule: Step 6 (RECRUITMENT_STOPPED) always allows editing match info and status
  if (progressStatus === CASE_STATUS.RECRUITMENT_STOPPED) {
    return true;
  }

  // Rule: If all case matches are ALREADY_HIRED (入社済み) → no edit
  if (caseInfo.isAllAlreadyHired) {
    return false;
  }

  // Rule: WAITING_FOR_JOINING (5) special cases
  if (progressStatus === CASE_STATUS.WAITING_FOR_JOINING) {
    // If all are ALREADY_HIRED or failed (no OFFER_ACCEPTED) → no edit
    if (caseInfo.isAllMatchesHired && !caseInfo.hasOfferAccepted) {
      return false;
    }
    // If there's mix of failed and OFFER_ACCEPTED/ALREADY_HIRED → allows edit
    if (caseInfo.hasFailedAndSuccessful) {
      return true;
    }
  }

  return true;
};
